import React, { useRef, useState } from 'react';
import Chip from '../../Chip/Chip';
import Dropdown from '../../Dropdown/Dropdown';
import './TeacherFilters.css';
import { FilterKey, filtersConfig } from './filtersConfig';
import { useTeacherSearchContext } from '../../../providers/TeacherSearchProvider';
import { TeachersUIFilters } from '../../../types/TeachersUIFilters';

const TeacherFilters: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
  const { filters, setFilters, clearFilter } = useTeacherSearchContext();
  const chipContainerRef = useRef<HTMLDivElement>(null);

  const toggleFilterDropdown = (key: FilterKey) => {
    setActiveFilter((prev) => (prev === key ? null : key));
  };

  const handleSelectOption = (
    filterKey: keyof TeachersUIFilters,
    selectedOption: string,
    isMultiSelect: boolean
  ) => {
    const currentValues = filters[filterKey];

    let newValues: string[] = [];

    if (isMultiSelect) {
      const current = (currentValues as string[] | undefined) ?? [];

      newValues = current.includes(selectedOption)
        ? current.filter((v) => v !== selectedOption)
        : [...current, selectedOption];
    } else {
      newValues = [selectedOption];
    }

    setFilters({ [filterKey]: isMultiSelect ? newValues : newValues[0] });
  };

  const getOptionDisplayName = (key: string, value: string): string => {
    const filter = filtersConfig.find((f) => f.key === key);

    if (filter?.type === 'select' && filter.displayMap?.[value]) {
      return filter.displayMap[value];
    }

    return value;
  };

  const renderFilterDropdown = (filterKey: string) => {
    const filterCfg = filtersConfig.find((f) => f.key === filterKey);

    if (!filterCfg) {
      return null;
    }

    if (filterCfg.type === 'custom' && filterCfg.customComponent) {
      const CustomComponent = filterCfg.customComponent;

      return (
        <div className="filterContent">
          <button className="closeButton" onClick={() => setActiveFilter(null)}>
            ✕
          </button>
          <span className="filterTitle">{filterCfg.label}</span>
          <CustomComponent filters={filters} onChange={setFilters} />
          <div className="filterActions">
            <button
              className="clearButton"
              onClick={() => {
                filterCfg?.customFilterKeys?.forEach((filter) => clearFilter(filter));
              }}>
              Borrar
            </button>
          </div>
        </div>
      );
    }

    if (filterCfg.type === 'select') {
      const selected = filters[filterCfg.key as keyof TeachersUIFilters];
      const selectedValues = Array.isArray(selected)
        ? selected.map((v) => String(v))
        : selected !== undefined
        ? [String(selected)]
        : [];

      return (
        <div className="filterContent">
          <button className="closeButton" onClick={() => setActiveFilter(null)}>
            ✕
          </button>
          <span className="filterTitle">{filterCfg.label}</span>
          <div className="filterOptions">
            {filterCfg.options?.map((option) => (
              <Chip
                key={option}
                label={getOptionDisplayName(filterCfg.key, option)}
                active={selectedValues.includes(option)}
                onClick={() =>
                  handleSelectOption(
                    filterKey as keyof TeachersUIFilters,
                    option,
                    filterCfg.multiSelect
                  )
                }
              />
            ))}
          </div>
          <div className="filterActions">
            <button
              className="clearButton"
              onClick={() => clearFilter(filterKey as keyof TeachersUIFilters)}>
              Borrar
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="filtersContainer">
      <div className="filtersScroller" ref={chipContainerRef}>
        {filtersConfig
          .filter((filterCnf) => !filterCnf.hide)
          .map((filterCnf) => {
            const isActive = Boolean(
              (filterCnf.type === 'select' && filters[filterCnf.key as keyof TeachersUIFilters]) ||
                (filterCnf.type === 'custom' &&
                  filterCnf.customFilterKeys?.find((key) => filters[key]))
            );

            return (
              <Chip
                key={filterCnf.key}
                label={filterCnf.label}
                active={isActive}
                selected={activeFilter === filterCnf.key}
                withArrow={true}
                onClick={() => toggleFilterDropdown(filterCnf.key)}
              />
            );
          })}
      </div>

      {activeFilter && (
        <div className="filtersDropdownContainer">
          <Dropdown onClose={() => setActiveFilter(null)} ignoreRefs={[chipContainerRef]}>
            {renderFilterDropdown(activeFilter)}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

export default TeacherFilters;
